import {
    CircleConsentConfigScopeEnum,
    CircleConsentTypes,
} from '@calm/evexia-web-sdk';
import { ConsentContext } from '@rhg/providers/registration/consent';
import { useContext, useState } from 'react';
import {
    AccountConsentConfigs,
    AccountConsentTypes,
    AccountConsentVersions,
    CircleConsentConfigs,
    ConsentAcceptance,
    ConsentContextProps,
    ConsentContextState,
    ConsentFlow,
    ConsentRequirement,
    ConsentTypes,
    ConsentVersions,
    OverlappingConsentTypes,
} from './types';

function makeDefaultCircleConsentConfigs(): CircleConsentConfigs {
    return Object.values(CircleConsentTypes).reduce((acc, type) => {
        acc[type] = undefined;
        return acc;
    }, {} as CircleConsentConfigs);
}

function makeDefaultAccountConsentConfigs(): AccountConsentConfigs {
    return Object.values(AccountConsentTypes).reduce((acc, type) => {
        acc[type] = undefined;
        return acc;
    }, {} as AccountConsentConfigs);
}

function makeDefaultConsentAcceptance(): ConsentAcceptance {
    return Object.values(ConsentTypes).reduce((acc, type) => {
        acc[type] = false;
        return acc;
    }, {} as ConsentAcceptance);
}

function makeDefaultAccountConsentVersions(): AccountConsentVersions {
    return Object.values(AccountConsentTypes).reduce((acc, type) => {
        acc[type] = 0;
        return acc;
    }, {} as AccountConsentVersions);
}

/** Determines which consents are required based on the user's account, sponsor, and already accepted consents */
export function getConsentContext({
    accountConsents,
    accountUserConsents,
    sponsorConsents,
    currentSponsorConsents,
    shouldUseCurrentSponsor,
}: ConsentContextProps): ConsentRequirement {
    const sponsorConsentConfigs =
        sponsorConsents?.reduce<CircleConsentConfigs>(
            (consentConfigs, consentConfig) => {
                const { type, version } = consentConfig;
                // Use the consent config with the highest version and acceptedVersion
                if (
                    !consentConfigs[type] ||
                    consentConfigs[type].version < version ||
                    (consentConfigs[type].version === version &&
                        (consentConfigs[type]?.acceptedVersion ?? 0) <
                            (consentConfig.acceptedVersion ?? 0))
                ) {
                    consentConfigs[type] = consentConfig;
                }

                return consentConfigs;
            },
            makeDefaultCircleConsentConfigs()
        ) ?? makeDefaultCircleConsentConfigs();

    const accountConsentConfigs = accountConsents.reduce<AccountConsentConfigs>(
        (consentConfigs, consent) => {
            if (
                !consentConfigs.tos ||
                (consent?.minTosVersion ?? 0) > consentConfigs.tos.version
            ) {
                consentConfigs.tos = {
                    version: consent.minTosVersion ?? 0,
                    link: consent.tosLink ?? '',
                };
            }
            if (
                !consentConfigs.privacy ||
                (consent?.minPrivacyVersion ?? 0) >
                    consentConfigs.privacy.version
            ) {
                consentConfigs.privacy = {
                    version: consent.minPrivacyVersion ?? 0,
                    link: consent.privacyLink ?? '',
                };
            }
            if (
                !consentConfigs.chdpp ||
                (consent?.minCHDPPVersion ?? 0) > consentConfigs.chdpp.version
            ) {
                consentConfigs.chdpp = {
                    version: consent.minCHDPPVersion ?? 0,
                    link: consent.chdppLink ?? '',
                };
            }
            if (
                !consentConfigs.parental ||
                (consent?.minParentalVersion ?? 0) >
                    consentConfigs.parental.version
            ) {
                consentConfigs.parental = {
                    version: consent.minParentalVersion ?? 0,
                    link: consent.ukInternationalLink ?? '',
                };
            }
            if (
                !consentConfigs.uk_international ||
                (consent?.minUKInternationalVersion ?? 0) >
                    consentConfigs.uk_international.version
            ) {
                consentConfigs.uk_international = {
                    version: consent.minUKInternationalVersion ?? 0,
                    link: consent.ukInternationalLink ?? '',
                };
            }
            if (
                !consentConfigs.in_international ||
                (consent?.minINInternationalVersion ?? 0) >
                    consentConfigs.in_international.version
            ) {
                consentConfigs.in_international = {
                    version: consent.minINInternationalVersion ?? 0,
                    link: consent.inInternationalLink ?? '',
                };
            }
            if (
                !consentConfigs.cn_international ||
                (consent?.minCNInternationalVersion ?? 0) >
                    consentConfigs.cn_international.version
            ) {
                consentConfigs.cn_international = {
                    version: consent.minCNInternationalVersion ?? 0,
                    link: consent.cnInternationalLink ?? '',
                };
            }
            if (
                !consentConfigs.eu_international ||
                (consent?.minEUInternationalVersion ?? 0) >
                    consentConfigs.eu_international.version
            ) {
                consentConfigs.eu_international = {
                    version: consent.minEUInternationalVersion ?? 0,
                    link: consent.euInternationalLink ?? '',
                };
            }
            if (
                !consentConfigs.au_international ||
                (consent?.minAuInternationalVersion ?? 0) >
                    consentConfigs.au_international.version
            ) {
                consentConfigs.au_international = {
                    version: consent.minAuInternationalVersion ?? 0,
                    link: consent.auInternationalLink ?? '',
                };
            }
            if (
                !consentConfigs.international ||
                (consent?.minInternationalVersion ?? 0) >
                    consentConfigs.international.version
            ) {
                consentConfigs.international = {
                    version: consent.minInternationalVersion ?? 0,
                    link: consent.internationalLink ?? '',
                };
            }

            return consentConfigs;
        },
        makeDefaultAccountConsentConfigs()
    );

    const userAccountConsentVersion =
        accountUserConsents.reduce<AccountConsentVersions>(
            (maxConsentedVersion, consent) => ({
                tos: Math.max(maxConsentedVersion.tos, consent.tosVersion ?? 0),
                privacy: Math.max(
                    maxConsentedVersion.privacy,
                    consent.privacyVersion ?? 0
                ),
                chdpp: Math.max(
                    maxConsentedVersion.chdpp,
                    consent.chdppVersion ?? 0
                ),
                uk_international: Math.max(
                    maxConsentedVersion.uk_international,
                    consent.ukInternationalVersion ?? 0
                ),
                parental: Math.max(
                    maxConsentedVersion.parental,
                    consent.parentalVersion ?? 0
                ),
                in_international: Math.max(
                    maxConsentedVersion.in_international,
                    consent.inInternationalVersion ?? 0
                ),
                cn_international: Math.max(
                    maxConsentedVersion.cn_international,
                    consent.cnInternationalVersion ?? 0
                ),
                eu_international: Math.max(
                    maxConsentedVersion.eu_international,
                    consent.euInternationalVersion ?? 0
                ),
                au_international: Math.max(
                    maxConsentedVersion.au_international,
                    consent.auInternationalVersion ?? 0
                ),
                international: Math.max(
                    maxConsentedVersion.international,
                    consent.internationalVersion ?? 0
                ),
            }),
            makeDefaultAccountConsentVersions()
        );

    return Object.values(ConsentTypes).reduce<ConsentRequirement>(
        (consentContext, consentType: ConsentTypes) => {
            let requiredVersion = 0;
            let acceptedVersion = 0;
            let link = '';
            let scope: CircleConsentConfigScopeEnum = 'account';

            const isCircleConsent = Object.values(CircleConsentTypes).includes(
                consentType as CircleConsentTypes
            );
            const isOverlappingConsent = Object.values(
                OverlappingConsentTypes
            ).includes(consentType as OverlappingConsentTypes);
            const shouldUseSponsorConsent =
                isCircleConsent &&
                shouldUseCurrentSponsor?.includes(
                    consentType as CircleConsentTypes
                );

            if (shouldUseSponsorConsent) {
                // Use the current sponsor's consent config if the consent type is a circle consent and shouldUseCurrentSponsor includes this type.
                const sponsorConsent = currentSponsorConsents?.find(
                    (consent) => consent.type === consentType
                );

                requiredVersion = sponsorConsent?.version ?? 0;
                acceptedVersion = sponsorConsent?.acceptedVersion ?? 0;
                link = sponsorConsent?.url ?? '';
                scope = sponsorConsent?.scope ?? 'account';
            } else if (isOverlappingConsent) {
                // For overlapping consents, prefer sponsor consent if any sponsor consents exist, otherwise fall back to the account consent config.
                const overlappingType = consentType as OverlappingConsentTypes;
                const sponsorConsentConfig =
                    sponsorConsentConfigs[overlappingType];
                const accountConsentConfig =
                    accountConsentConfigs[overlappingType];
                const hasSponsorConsents = !!sponsorConsents;
                requiredVersion = hasSponsorConsents
                    ? sponsorConsentConfig?.version ?? 0
                    : accountConsentConfig?.version ?? 0;
                acceptedVersion = Math.max(
                    sponsorConsentConfig?.acceptedVersion ?? 0,
                    userAccountConsentVersion[overlappingType]
                );
                link = hasSponsorConsents
                    ? sponsorConsentConfig?.url ?? ''
                    : accountConsentConfig?.link ?? '';
                scope = sponsorConsentConfig?.scope ?? 'account';
            } else if (isCircleConsent) {
                // For other circle consents, use the sponsor/circle consent config if it exists.
                const circleType = consentType as CircleConsentTypes;
                const circleConsentConfig = sponsorConsentConfigs[circleType];

                requiredVersion = circleConsentConfig?.version ?? 0;
                acceptedVersion = circleConsentConfig?.acceptedVersion ?? 0;
                link = circleConsentConfig?.url ?? '';
                scope = circleConsentConfig?.scope ?? 'account';
            } else {
                // For all other cases, use the account consent config.
                const accountType = consentType as AccountConsentTypes;
                const accountConsentConfig = accountConsentConfigs[accountType];

                requiredVersion = accountConsentConfig?.version ?? 0;
                acceptedVersion = userAccountConsentVersion[accountType] ?? 0;
                link = accountConsentConfig?.link ?? '';
            }

            consentContext[consentType] = {
                consentType,
                acceptedVersion,
                requiredVersion,
                link,
                scope,
                isRequired: requiredVersion > acceptedVersion,
                hasRequirement: requiredVersion > 0,
                isReconsent:
                    !!acceptedVersion && requiredVersion > acceptedVersion,
            };
            return consentContext;
        },
        {} as ConsentRequirement
    );
}

export function getConsentSubmission({
    consentRequirements,
    consentTypes = ConsentFlow,
}: {
    consentRequirements: ConsentRequirement;
    consentTypes?: ConsentTypes[];
}) {
    return Object.values(consentRequirements).reduce<ConsentVersions>(
        (consentVersions, { consentType, requiredVersion }) => {
            if (requiredVersion && consentTypes.includes(consentType)) {
                consentVersions[consentType] = requiredVersion;
            }
            return consentVersions;
        },
        {} as ConsentVersions
    );
}

/**
 * loop through the consentFlow array and display the first one that does not satisfy the requirements
 */
export function getNextConsentType({
    consentRequirements,
    consentAcceptance,
    consentFlow = ConsentFlow,
    reverse = false,
}: {
    consentRequirements: ConsentRequirement;
    /** Additional map of accepted/available consents */
    consentAcceptance?: ConsentAcceptance;
    consentFlow?: ConsentTypes[];
    /** Whether the consent needs to just have a required version (false), or whether it needs to have the required version > accepted version (true) */
    needsRequired?: boolean;
    reverse?: boolean;
}) {
    return (reverse ? consentFlow.toReversed() : consentFlow).find(
        (consentType) =>
            consentRequirements[consentType] &&
            consentRequirements[consentType].hasRequirement &&
            consentRequirements[consentType].isRequired &&
            !consentAcceptance?.[consentType]
    );
}

export function getNextConsentTypeInFlow(
    consentType: ConsentTypes,
    consentFlow = ConsentFlow
) {
    const currentIndex = consentFlow.indexOf(consentType as CircleConsentTypes);
    return currentIndex !== -1 ? consentFlow.slice(currentIndex + 1) : [];
}

export function useConsentContext(
    props: ConsentContextProps
): ConsentContextState {
    const [hasAcceptedConsent, _setHasAcceptedConsent] = useState(
        makeDefaultConsentAcceptance()
    );
    const setHasAcceptedConsent = (type: ConsentTypes, value: boolean) =>
        _setHasAcceptedConsent((prev) => ({
            ...prev,
            [type]: value,
        }));

    const consentRequirements = getConsentContext(props);

    return {
        consentRequirements,
        nextConsentType: getNextConsentType({
            consentRequirements,
            consentAcceptance: hasAcceptedConsent,
        }),
        hasAcceptedConsent,
        setHasAcceptedConsent,
    };
}

/** Uses context from the closest <ConsentContextProvider> */
export function useProvidedConsentContext() {
    return useContext(ConsentContext);
}
